import {
  COMPARISON_ENTITIES_URL_PARAM,
  DETAILS_PAGE_URL_DISCRIMINATOR_PARAM,
} from './constants';
import { selectionToKeyValuePairs } from './filtersToKeyValuePairs';

export const MIN_COMPARISON_ENTITY_COUNT = 2;

/**
 * Parses repeated or comma-separated raw URL param value strings into distinct,
 * trimmed, non-empty values.
 */
export function parseDelimitedParamValues(rawValues: string[]): string[] {
  return [
    ...new Set(
      rawValues
        .flatMap((value) => value.split(','))
        .map((part) => part.trim())
        .filter(Boolean)
    ),
  ];
}

/** Reads and parses all values for a search param key. */
export function parseSearchParamValues(
  searchParams: URLSearchParams,
  searchKey: string
): string[] {
  return parseDelimitedParamValues(searchParams.getAll(searchKey));
}

/** Reads, parses, and stringifies all values for a search param key. */
export function stringifySearchParams(
  searchParams: URLSearchParams,
  searchKey: string
): string {
  return parseSearchParamValues(searchParams, searchKey).join(',');
}

/** Returns a sorted, deduplicated comma-separated entity pool from `dp_entities`. */
export function stringifyComparisonEntityPool(
  searchParams: URLSearchParams
): string {
  return [
    ...parseSearchParamValues(searchParams, COMPARISON_ENTITIES_URL_PARAM),
  ]
    .sort()
    .join(',');
}

/** Returns distinct, sorted entity IDs from `dp_entities`. */
export function parseComparisonEntityPool(
  searchParams: URLSearchParams
): string[] {
  const stringified = stringifyComparisonEntityPool(searchParams);
  return stringified === '' ? [] : stringified.split(',');
}

/**
 * Default comparison pair after reset: the first two IDs from the sorted
 * `dp_entities` pool (same ordering as `stringifyComparisonEntityPool`).
 */
export function getDefaultComparisonPair(
  searchParams: URLSearchParams
): [string, string] | undefined {
  const pool = parseComparisonEntityPool(searchParams);
  if (pool.length < MIN_COMPARISON_ENTITY_COUNT) {
    return undefined;
  }
  return [pool[0], pool[1]];
}

/** Returns the URL discriminator pair when it is a valid comparison selection. */
export function getValidComparisonPairFromUrl(
  searchParams: URLSearchParams,
  discriminatorKey: string
): [string, string] | undefined {
  const values = parseComparisonDiscriminatorValues(
    searchParams,
    discriminatorKey
  );
  if (values.length !== MIN_COMPARISON_ENTITY_COUNT) {
    return undefined;
  }

  const pair = values as [string, string];
  return isValidComparisonSelection(searchParams, pair) ? pair : undefined;
}

export function isValidComparisonSelection(
  searchParams: URLSearchParams,
  selection: unknown
): selection is [string, string] {
  if (
    !Array.isArray(selection) ||
    selection.length !== MIN_COMPARISON_ENTITY_COUNT
  ) {
    return false;
  }

  if (
    !selection.every((value) => typeof value === 'string' && value.length > 0)
  ) {
    return false;
  }

  if (new Set(selection).size !== MIN_COMPARISON_ENTITY_COUNT) {
    return false;
  }

  const pool = new Set(parseComparisonEntityPool(searchParams));
  return selection.every((value) => pool.has(value));
}

/**
 * Reads comparison discriminator values from the URL, supporting repeated keys
 * and legacy comma-separated values, and returns distinct non-empty IDs.
 */
export function parseComparisonDiscriminatorValues(
  searchParams: URLSearchParams,
  discriminatorKey: string
): string[] {
  return parseSearchParamValues(searchParams, discriminatorKey);
}

export function assertValidComparisonDiscriminatorValues(
  searchParams: URLSearchParams,
  discriminatorKey: string
): void {
  const values = parseSearchParamValues(searchParams, discriminatorKey);

  if (values.length !== MIN_COMPARISON_ENTITY_COUNT) {
    throw new Error(
      `Comparison detail pages require the discriminator key ("${DETAILS_PAGE_URL_DISCRIMINATOR_PARAM}") with exactly ${MIN_COMPARISON_ENTITY_COUNT} distinct values.`
    );
  }

  const pool = new Set(parseComparisonEntityPool(searchParams));
  const outsidePool = values.filter((value) => !pool.has(value));
  if (outsidePool.length > 0) {
    throw new Error(
      `Comparison discriminator value(s) "${outsidePool.join('", "')}" are not present in "${COMPARISON_ENTITIES_URL_PARAM}".`
    );
  }
}

/** Validates comparison detail page URL params for filter loading. */
export function validateComparisonDetailPageUrl(
  searchParams: URLSearchParams,
  discriminatorKey: string
): void {
  const pool = parseComparisonEntityPool(searchParams);
  if (pool.length < MIN_COMPARISON_ENTITY_COUNT) {
    throw new Error(
      `At least ${MIN_COMPARISON_ENTITY_COUNT} entity IDs are required for comparison, but only ${pool.length} were provided.`
    );
  }

  assertValidComparisonDiscriminatorValues(searchParams, discriminatorKey);
}

/**
 * Appends the first two entity IDs as repeated discriminator keys and
 * `dp_entities=<id1>,<id2>,...` to an existing URL string.
 *
 * @throws when fewer than two entity IDs are provided.
 */
export function buildComparisonDetailPageUrl(
  baseUrl: string,
  identifierColumn: string,
  entityIds: string[]
): string {
  if (entityIds.length < MIN_COMPARISON_ENTITY_COUNT) {
    throw new Error(
      `At least ${MIN_COMPARISON_ENTITY_COUNT} entity IDs are required to build a comparison detail page URL, but only ${entityIds.length} were provided.`
    );
  }

  const discriminatorPairs = selectionToKeyValuePairs(
    identifierColumn,
    entityIds.slice(0, 2)
  );
  const parts: string[] = [];
  if (discriminatorPairs.length > 0) {
    parts.push(new URLSearchParams(discriminatorPairs).toString());
  }
  parts.push(`${COMPARISON_ENTITIES_URL_PARAM}=${entityIds.join(',')}`);

  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}${parts.join('&')}`;
}
